import { useEffect, useMemo, useRef, useState } from 'react';
import {
  VizCard,
  NowYouTry,
  VIZ_FOCUS,
  usePrefersReducedMotion,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from '../vizShared';
import { BY_SYMBOL, BY_Z, type ElementInfo } from '../elements';

// LewisDots — the classic single-atom Lewis electron-dot diagram: an element
// symbol with its VALENCE electrons drawn as dots around the four sides. This is
// for teaching how to DRAW Lewis dot diagrams for individual atoms (not whole
// molecules). Each valence electron is placed one per side first (top, right,
// bottom, left — up to 4 singles), then electrons 5–8 PAIR up by adding a second
// dot to a side, the standard Lewis convention.
//   B(3)=3 singles · C(4)=4 singles · N(5)=1 pair+3 singles · O(6)=2 pairs+2
//   singles · F(7)=3 pairs+1 single · Ne(8)=octet · H(1)=1 · Cl(7)=3 pairs+1.
// config: { elements?: string[] (1–2 symbols/names/Z; default ['C']),
//           element?: string (alias for a single element), title? }.
// Two elements render side-by-side to contrast their valence counts.
// Cue: cue.progress / cue.step reveal the dots one at a time around the current
// atom; cue.highlight names a symbol to spotlight (dim the other). Reduced
// motion ⇒ full static diagram. Self-demo places dots one-by-one, then NowYouTry.

export interface LewisDotsConfig {
  /** 1 or 2 elements (symbol / name / atomic number). Default ['C']. */
  elements?: string[];
  /** Alias for a single element (used if `elements` is absent). */
  element?: string;
  title?: string;
}

interface Props {
  config?: LewisDotsConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

// ---------------------------------------------------------------------------
// Element resolution — accept a symbol ("C"), a name ("carbon") or an atomic
// number ("6" / 6). Always returns a real element; falls back to carbon.
// ---------------------------------------------------------------------------
const BY_NAME: Record<string, ElementInfo> = Object.fromEntries(
  Object.values(BY_SYMBOL).map((e) => [e.name.toLowerCase(), e]),
);

function resolveElement(input?: string | number): ElementInfo {
  if (input == null) return BY_SYMBOL.C;
  const raw = String(input).trim();
  if (!raw) return BY_SYMBOL.C;
  // Atomic number?
  if (/^\d+$/.test(raw)) {
    const z = parseInt(raw, 10);
    if (BY_Z[z]) return BY_Z[z];
  }
  // Exact symbol (case-insensitive: "cl" → "Cl").
  const cap = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  if (BY_SYMBOL[cap]) return BY_SYMBOL[cap];
  if (BY_SYMBOL[raw]) return BY_SYMBOL[raw];
  // Element name.
  if (BY_NAME[raw.toLowerCase()]) return BY_NAME[raw.toLowerCase()];
  return BY_SYMBOL.C;
}

// ---------------------------------------------------------------------------
// Valence electrons from the main-group number. Returns null for elements whose
// valence isn't well-defined by the simple main-group rule (transition/inner
// transition / unknown) so we can render a best-effort "—" instead of dots.
// ---------------------------------------------------------------------------
function valenceElectrons(el: ElementInfo): number | null {
  if (el.symbol === 'H') return 1;
  if (el.symbol === 'He') return 2;
  const g = el.group;
  if (g == null) return null; // f-block (lanthanide / actinide)
  if (g === 1 || g === 2) return g; // groups 1–2 → 1 or 2
  if (g >= 13 && g <= 18) return g - 10; // groups 13–18 → 3..8 (17→7, 18→8)
  // Groups 3–12 are the transition metals — no simple Lewis valence.
  return null;
}

// ---------------------------------------------------------------------------
// Build the dot layout. Sides are ordered top, right, bottom, left. We place ONE
// dot per side first (so 1–4 electrons are all singles), then add a SECOND dot to
// each side in the same order to pair them (electrons 5–8). The returned array
// has one entry per electron, in placement order, each tagged with its side and
// slot (0 = first dot on that side, 1 = the pairing dot).
// ---------------------------------------------------------------------------
type Side = 'top' | 'right' | 'bottom' | 'left';
const SIDE_ORDER: Side[] = ['top', 'right', 'bottom', 'left'];

interface DotSpec {
  side: Side;
  slot: 0 | 1; // 0 = first electron on this side, 1 = its pair
}

function buildDots(valence: number): DotSpec[] {
  const n = Math.max(0, Math.min(8, Math.floor(valence)));
  const dots: DotSpec[] = [];
  // First pass: singles (electrons 1..4, one per side).
  for (let i = 0; i < Math.min(4, n); i++) {
    dots.push({ side: SIDE_ORDER[i], slot: 0 });
  }
  // Second pass: pairing dots (electrons 5..8, second dot per side).
  for (let i = 4; i < n; i++) {
    dots.push({ side: SIDE_ORDER[i - 4], slot: 1 });
  }
  return dots;
}

// Pixel offset (from the symbol's center box edge) of each dot. Two slots per
// side are offset to either side of the side's midpoint so a pair reads as two
// distinct dots. Coordinates are relative to the atom's local center.
function dotPosition(spec: DotSpec, box: number, gap: number): { x: number; y: number } {
  const edge = box / 2 + gap; // distance from center to the dot band
  const spread = 7; // half-distance between the two dots of a pair
  // For a single dot (slot 0 with no pair yet) we keep it centered on the side;
  // but since pairing always adds slot 1 alongside, we offset slot 0 to the left/
  // up side and slot 1 to the right/down side. A lone single still looks centered
  // because the offset is small relative to the symbol.
  const off = spec.slot === 0 ? -spread : spread;
  switch (spec.side) {
    case 'top':
      return { x: off, y: -edge };
    case 'bottom':
      return { x: off, y: edge };
    case 'left':
      return { x: -edge, y: off };
    case 'right':
      return { x: edge, y: off };
  }
}

const GROUP_LABEL: Record<number, string> = {
  1: 'Group 1', 2: 'Group 2', 13: 'Group 13', 14: 'Group 14',
  15: 'Group 15', 16: 'Group 16', 17: 'Group 17', 18: 'Group 18',
};

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

export default function LewisDots({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const cueOn = cueIsActive(cue);

  // --- Resolve the 1–2 atoms to draw. ----------------------------------------
  const atoms = useMemo<ElementInfo[]>(() => {
    let inputs: (string | number)[] = [];
    if (Array.isArray(config.elements) && config.elements.length) {
      inputs = config.elements.filter((s) => s != null && String(s).trim() !== '');
    } else if (config.element != null && String(config.element).trim() !== '') {
      inputs = [config.element];
    }
    if (!inputs.length) inputs = ['C'];
    const resolved = inputs.slice(0, 2).map(resolveElement);
    return resolved.length ? resolved : [BY_SYMBOL.C];
  }, [config.elements, config.element]);

  // Valence per atom (null ⇒ no simple Lewis structure).
  const valences = useMemo(() => atoms.map(valenceElectrons), [atoms]);
  const dotsPerAtom = useMemo(
    () => valences.map((v) => (v == null ? [] : buildDots(v))),
    [valences],
  );
  const maxDots = useMemo(
    () => dotsPerAtom.reduce((m, d) => Math.max(m, d.length), 0),
    [dotsPerAtom],
  );

  // --- Reveal state: how many dots are placed (around EACH atom in lockstep). -
  const fullRevealed = maxDots;
  const [revealed, setRevealed] = useState<number>(reduced || cueOn ? fullRevealed : 0);
  // Which atom symbol is spotlighted (others dim). null ⇒ all equally lit.
  const [spotlight, setSpotlight] = useState<string | null>(null);
  const [showTry, setShowTry] = useState(false);
  const interactedRef = useRef(false);
  const timers = useRef<number[]>([]);

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  };

  // --- Self-demo: place the dots one at a time, then offer "Now you try". -----
  useEffect(() => {
    clearTimers();
    if (reduced || cueOn) {
      setRevealed(fullRevealed);
      setSpotlight(null);
      setShowTry(false);
      return clearTimers;
    }
    setRevealed(0);
    setShowTry(false);
    const tl: number[] = [];
    let at = 450;
    const STEP = 360;
    for (let i = 1; i <= fullRevealed; i++) {
      tl.push(window.setTimeout(() => setRevealed(i), at));
      at += STEP;
    }
    tl.push(window.setTimeout(() => setShowTry(true), at + 120));
    timers.current = tl;
    return clearTimers;
    // Re-arm if the set of atoms changes (e.g. new config).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduced, cueOn, fullRevealed]);

  // --- Cue: progress / step drive the reveal; highlight spotlights an atom. ---
  useEffect(() => {
    if (!cue) return;
    clearTimers();
    setShowTry(false);

    const tokens = cueHighlights(cue);
    if (tokens.length) {
      const hit = atoms.find(
        (a) => tokens.includes(a.symbol.toLowerCase()) || tokens.includes(a.name.toLowerCase()),
      );
      setSpotlight(hit ? hit.symbol : null);
    } else {
      setSpotlight(null);
    }

    // Animate the dot-by-dot reveal ONLY while actively playing; at rest (paused —
    // e.g. a student landing on the slide) show ALL dots so the diagram is never
    // a bare symbol with no electrons. (Same principle as SkeletalStructure.)
    if (cue.play === true && typeof cue.step === 'number') {
      setRevealed(clamp(Math.round(cue.step), 0, fullRevealed));
    } else if (cue.play === true && typeof cue.progress === 'number') {
      setRevealed(clamp(Math.round(cue.progress * fullRevealed), 0, fullRevealed));
    } else {
      // paused / highlight-only / no progress → full diagram, never bare symbols.
      setRevealed(fullRevealed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cue, fullRevealed]);

  const handleInteract = (symbol: string) => {
    clearTimers();
    setShowTry(false);
    setRevealed(fullRevealed);
    setSpotlight((cur) => (cur === symbol ? null : symbol));
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'lewis_dots', element: symbol });
    }
  };

  const subtitle =
    atoms.length === 2
      ? `Compare the valence electrons of ${atoms[0].name} and ${atoms[1].name}.`
      : `Place ${atoms[0].name}'s valence electrons around the symbol — singles first, then pairs.`;

  return (
    <VizCard
      eyebrow="Bonding · Lewis dot diagram"
      title={config.title ?? (atoms.length === 2 ? 'Lewis dot diagrams' : `Lewis dot diagram — ${atoms[0].name}`)}
      subtitle={subtitle}
      reducedMotion={reduced}
      hint="One dot per side first (up to four), then pair them up — that's the Lewis convention for valence electrons."
    >
      <div className="relative flex w-full flex-col gap-3">
        <NowYouTry show={showTry && !reduced && !cueOn} />
        <div
          className={`grid gap-3 ${atoms.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}
        >
          {atoms.map((atom, idx) => (
            <AtomCell
              key={`${atom.symbol}-${idx}`}
              atom={atom}
              valence={valences[idx]}
              dots={dotsPerAtom[idx]}
              revealed={revealed}
              dim={spotlight != null && spotlight !== atom.symbol}
              lit={spotlight === atom.symbol}
              reduced={reduced}
              onClick={() => handleInteract(atom.symbol)}
            />
          ))}
        </div>
      </div>
    </VizCard>
  );
}

// ---------------------------------------------------------------------------
// A single atom tile: the element symbol centered in a box, valence dots placed
// around it (revealed up to `revealed`), and a caption with the valence count +
// group label. Clicking spotlights this atom.
// ---------------------------------------------------------------------------
const VIEW = 200;
const C = VIEW / 2;
const BOX = 58; // symbol "box" the dots orbit around
const GAP = 14; // distance from box edge to the dot band
const DOT_R = 4.5;

function AtomCell({
  atom,
  valence,
  dots,
  revealed,
  dim,
  lit,
  reduced,
  onClick,
}: {
  atom: ElementInfo;
  valence: number | null;
  dots: DotSpec[];
  revealed: number;
  dim: boolean;
  lit: boolean;
  reduced: boolean;
  onClick: () => void;
}) {
  const group = atom.group;
  const groupLabel = group != null ? GROUP_LABEL[group] : null;
  const shown = Math.min(revealed, dots.length);

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${atom.name} Lewis dot diagram`}
      className={`group relative flex flex-col items-center rounded-md border bg-ws-surface p-3 transition-all ${VIZ_FOCUS} ${
        lit ? 'border-ws-accent shadow-sm' : 'border-ws-border hover:border-ws-border-strong'
      }`}
      style={{
        opacity: dim ? 0.4 : 1,
        transform: lit && !reduced ? 'scale(1.02)' : 'scale(1)',
        transition: reduced
          ? undefined
          : 'opacity 300ms ease, transform 300ms ease, border-color 200ms ease',
      }}
    >
      <svg
        viewBox={`0 0 ${VIEW} ${VIEW}`}
        className="w-full max-w-[180px]"
        role="img"
        aria-label={
          valence == null
            ? `${atom.name}: valence not defined by main group`
            : `${atom.name} with ${valence} valence electron${valence === 1 ? '' : 's'}`
        }
      >
        {/* Element symbol, centered. */}
        <text
          x={C}
          y={C}
          textAnchor="middle"
          dominantBaseline="central"
          className="fill-ws-ink font-semibold"
          style={{ fontSize: 34, fontFamily: 'ui-sans-serif, system-ui, sans-serif' }}
        >
          {atom.symbol}
        </text>

        {valence == null ? (
          // No simple Lewis valence (transition / f-block / unknown): show a dash.
          <text
            x={C}
            y={C + 42}
            textAnchor="middle"
            className="fill-ws-ink-3 font-mono"
            style={{ fontSize: 16 }}
          >
            —
          </text>
        ) : (
          <DotMarks
            dots={dots}
            shown={shown}
            reduced={reduced}
          />
        )}
      </svg>

      <div className="mt-1 flex flex-col items-center gap-0.5 text-center">
        <span className="text-xs font-medium text-ws-ink-2">{atom.name}</span>
        <span className="text-[11px] text-ws-ink-3">
          {valence == null ? (
            <>no simple Lewis valence</>
          ) : (
            <>
              {valence} valence e⁻
              {groupLabel ? <> · {groupLabel}</> : null}
            </>
          )}
        </span>
      </div>
    </button>
  );
}

// Render the valence dots. We recenter any side that has only ONE electron so a
// lone single sits dead-center on its side; sides that hold a pair keep both dots
// offset to either side of the midpoint. Dots beyond `shown` are hidden.
function DotMarks({
  dots,
  shown,
  reduced,
}: {
  dots: DotSpec[];
  shown: number;
  reduced: boolean;
}) {
  // Which sides ended up with two electrons (a pair)? A side with only one
  // electron gets a dead-centered dot; a paired side keeps both dots offset.
  const perSide: Record<Side, number> = { top: 0, right: 0, bottom: 0, left: 0 };
  dots.forEach((d) => (perSide[d.side] += 1));

  return (
    <g>
      {dots.map((spec, i) => {
        const isPair = perSide[spec.side] === 2;
        const pos = isPair
          ? dotPosition(spec, BOX, GAP)
          : centeredPosition(spec.side, BOX, GAP);
        const visible = i < shown;
        return (
          <circle
            key={`${spec.side}-${spec.slot}-${i}`}
            cx={C + pos.x}
            cy={C + pos.y}
            r={DOT_R}
            className="fill-ws-accent"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible || reduced ? 'scale(1)' : 'scale(0.4)',
              transformOrigin: `${C + pos.x}px ${C + pos.y}px`,
              transition: reduced ? undefined : 'opacity 220ms ease, transform 220ms ease',
            }}
          />
        );
      })}
    </g>
  );
}

// A dead-centered dot on a given side (used when that side has only one electron).
function centeredPosition(side: Side, box: number, gap: number): { x: number; y: number } {
  const edge = box / 2 + gap;
  switch (side) {
    case 'top':
      return { x: 0, y: -edge };
    case 'bottom':
      return { x: 0, y: edge };
    case 'left':
      return { x: -edge, y: 0 };
    case 'right':
      return { x: edge, y: 0 };
  }
}
